import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TuneIcon from '@mui/icons-material/Tune';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { ChatTransport, Conversation } from '../../hooks/useChat';
import { riderLabel, systemLabel } from '../../hooks/chat/mappers';
import { ChatTagGlossary, tagCategory, tagLabel } from './tagGlossary';
import { formatAgo, formatDateTime, statusLabel } from './format';
import { SLA_CHIP_COLOR, SLA_TICK_MS, queueWait, waitingSince } from './sla';
import { compareConversations } from '../../hooks/chat/queueOrder';
import { SHOW_ASSISTANT } from './flags';

/** `assistant`: the sessions the assistant is answering, read-only. */
export type StatusFilter = 'all' | 'open' | 'in_progress' | 'closed' | 'assistant';

interface Props {
  /** The queue: every session not closed. */
  conversations: Conversation[];
  /** Recently closed sessions, loaded when the Closed filter is picked. */
  closedConversations: Conversation[];
  closedLoading: boolean;
  /** Sessions the assistant is answering (the Assistant filter). */
  assistantConversations?: Conversation[];
  /** False until ant has answered the first read of the assistant's sessions. */
  assistantLoaded?: boolean;
  statusFilter: StatusFilter;
  onStatusFilterChange: (status: StatusFilter) => void;
  isConnected: boolean;
  /** What feeds the list: the socket ("Live"), the 3 s polls or nothing. */
  transport?: ChatTransport;
  /** False until ant has answered the first read of the queue. */
  loaded?: boolean;
  myAdminId?: string | null;
  selectedId?: string;
  /**
   * The row the keyboard is on (the page's j/k): focused and brought into
   * view when it changes; the open conversation's row when unset.
   */
  cursorId?: string;
  unreadByConv: Record<string, number>;
  onSelect: (conv: Conversation) => void;
  /** The ids of the rows shown, in their order, whenever that changes (for j/k/n). */
  onOrderChange?: (ids: string[]) => void;
  /** CS's glossary, for the tag filter's labels and groups; null while it loads. */
  glossary?: ChatTagGlossary | null;
}

/** The keys the page answers to, as the "?" on the title lists them. */
export const KEYBOARD_SHORTCUTS: Array<[keys: string, does: string]> = [
  ['j / k', 'Move down / up the queue'],
  ['Enter / o', 'Open the conversation'],
  ['n', 'Open the next rider waiting'],
  ['a', 'Assign the open conversation to me'],
  ['e', 'End the open conversation'],
  ['Esc', 'Close the open conversation'],
];

/** One entry of the tag filter: a tag some listed session carries, or the filtered one. */
interface TagFilterOption {
  id: string;
  label: string;
  /** What the rows group by: the category's id, or "Not in glossary" for an old string. */
  groupId: string;
  /** The group's header: the category's name, or "Not in glossary". */
  group: string;
  /** Glossary order (category position, tag position); old strings last. */
  order: number;
}

const NOT_IN_GLOSSARY = 'Not in glossary';

const TRANSPORT: Record<
  ChatTransport,
  { label: string; color: 'success' | 'warning' | 'error'; title: string }
> = {
  socket: {
    label: 'Live',
    color: 'success',
    title:
      "Live: connected to ant's GraphQL subscriptions over a websocket. New sessions and messages appear as they happen; the queue is also re-read every 30 s as a safety net.",
  },
  polling: {
    label: 'Reconnecting',
    color: 'warning',
    title:
      'Reconnecting: the websocket to ant is down, so the queue and the open conversation are re-read every 3 s until it is back.',
  },
  connecting: {
    label: 'Connecting',
    color: 'warning',
    title: 'Connecting to ant…',
  },
  offline: {
    label: 'Offline',
    color: 'error',
    title: 'Offline: ant is not answering. The list may be out of date.',
  },
};

/** Re-renders every 30 s so "waiting" times stay current. */
function useNow(intervalMs = SLA_TICK_MS) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export { compareConversations };

/**
 * The row's name line and what it stands for: the rider's name or phone
 * once read; "Rider ·483a" (the tail of their short id), muted, while the
 * lookup is still out; "Unnamed rider" when ant had neither for them.
 */
export function rowName(
  conv: Pick<
    Conversation,
    'id' | 'user_name' | 'user_phone' | 'user_id' | 'conversation_id' | 'rider_read'
  >
): { label: string; state: 'named' | 'pending' | 'unnamed'; pending: boolean } {
  if (conv.user_name || conv.user_phone) {
    return { label: riderLabel(conv), state: 'named', pending: false };
  }
  if (conv.rider_read)
    return { label: 'Unnamed rider', state: 'unnamed', pending: false };
  const id = conv.user_id || conv.conversation_id || conv.id;
  return { label: `Rider ·${id.slice(0, 8).slice(-4)}`, state: 'pending', pending: true };
}

/** The first name of a shown name ("Ana R." → "Ana"). */
const firstName = (name: string | null | undefined): string =>
  (name ?? '').trim().split(/\s+/)[0] ?? '';

/**
 * The row's second line: the newest message, prefixed with who answered
 * when it was Support ("Ana: On my way") or the assistant; null when the
 * session has no message to show yet.
 */
export function previewLine(conv: Conversation): string | null {
  const last = conv.last_message;
  if (!last?.preview) return null;
  if (last.sender_type === 'admin' || last.sender_type === 'note') {
    return `${firstName(last.sender_name) || 'Support'}: ${last.preview}`;
  }
  if (last.sender_type === 'assistant') return `Assistant: ${last.preview}`;
  return last.preview;
}

const STATUS_CHIP: Record<string, 'success' | 'info' | 'default'> = {
  open: 'success',
  in_progress: 'info',
  closed: 'default',
};

export function ConversationList({
  conversations,
  closedConversations,
  closedLoading,
  assistantConversations = [],
  assistantLoaded = true,
  statusFilter,
  onStatusFilterChange,
  isConnected,
  transport = isConnected ? 'socket' : 'offline',
  loaded = true,
  myAdminId = null,
  selectedId,
  cursorId,
  unreadByConv,
  onSelect,
  onOrderChange,
  glossary = null,
}: Props) {
  const [search, setSearch] = useState('');
  // '' = every system the token can see; 'general' = sessions with no system.
  const [systemFilter, setSystemFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  // '' = anyone; 'me'; 'unassigned'; otherwise an admin id.
  const [agentFilter, setAgentFilter] = useState('');
  // The title-row toggles: riders waiting for an answer; chats assigned to me.
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  // The Tag and Agent row is folded away unless one of them is set.
  const [filtersOpen, setFiltersOpen] = useState(() => Boolean(tagFilter || agentFilter));
  const now = useNow();

  const source =
    statusFilter === 'closed'
      ? closedConversations
      : statusFilter === 'assistant'
      ? assistantConversations
      : conversations;

  const systems = useMemo(() => {
    const byId = new Map<string, string>();
    [...conversations, ...closedConversations].forEach((c) => {
      if (c.system_id) byId.set(c.system_id, systemLabel(c, 'Unnamed system'));
    });
    return Array.from(byId.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [conversations, closedConversations]);

  // Every system this list has named, so the System select keeps its value
  // (and its label) when the filtered system's last session closes: the
  // select must never show a value it has no option for.
  const systemNamesRef = useRef(new Map<string, string>());
  systems.forEach(([id, name]) => systemNamesRef.current.set(id, name));
  const systemOptions = useMemo(() => {
    if (!systemFilter || systemFilter === 'general') return systems;
    if (systems.some(([id]) => id === systemFilter)) return systems;
    return [
      ...systems,
      [systemFilter, systemNamesRef.current.get(systemFilter) ?? 'System'] as [
        string,
        string
      ],
    ];
  }, [systems, systemFilter]);

  // A row names its system only when the loaded sessions come from more than
  // one place (General counts as one): a single-system queue says nothing new.
  const multiSystem = useMemo(
    () =>
      systems.length +
        Number([...conversations, ...closedConversations].some((c) => !c.system_id)) >
      1,
    [systems, conversations, closedConversations]
  );

  const agents = useMemo(() => {
    const byId = new Map<string, string>();
    [...conversations, ...closedConversations].forEach((c) => {
      if (c.assigned_admin_id && c.assigned_admin_id !== myAdminId) {
        byId.set(c.assigned_admin_id, c.assigned_admin_name || 'Unnamed admin');
      }
    });
    return Array.from(byId.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [conversations, closedConversations, myAdminId]);

  // The tags on the listed sessions, labelled and grouped by category (by
  // id: two categories may share a name) in glossary order; old strings
  // under "Not in glossary", last. The filtered tag stays listed when no
  // listed session carries it any more, so the filter keeps its option.
  const tagOptions = useMemo<TagFilterOption[]>(() => {
    const ids = Array.from(
      new Set([...(tagFilter ? [tagFilter] : []), ...source.flatMap((c) => c.tags ?? [])])
    );
    const options = ids.map((id) => {
      const category = tagCategory(glossary, id);
      const tag = glossary?.tagsBySlug.get(id);
      return {
        id,
        label: tagLabel(glossary, id),
        groupId: category?.id ?? NOT_IN_GLOSSARY,
        group: category?.name ?? NOT_IN_GLOSSARY,
        order:
          category && tag
            ? category.position * 1000 + tag.position
            : Number.MAX_SAFE_INTEGER,
      };
    });
    return options.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [source, glossary, tagFilter]);
  const tagGroupNames = useMemo(
    () => new Map(tagOptions.map((o) => [o.groupId, o.group])),
    [tagOptions]
  );
  const tagValue = useMemo(
    () => tagOptions.find((o) => o.id === tagFilter) ?? null,
    [tagOptions, tagFilter]
  );

  // The open conversation's row, brought into view once per selection: a
  // link lands on a session the agent has not scrolled to, and a poll that
  // reorders the list must not pull the agent back to it.
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  // One ref callback per row id, kept across renders: a new function each
  // render would make React detach and reattach every row on every poll.
  const rowRefCallbacks = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  const rowRef = (id: string) => {
    let callback = rowRefCallbacks.current.get(id);
    if (!callback) {
      callback = (el: HTMLDivElement | null) => {
        if (el) rowRefs.current.set(id, el);
        else rowRefs.current.delete(id);
      };
      rowRefCallbacks.current.set(id, callback);
    }
    return callback;
  };
  const scrolledToRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedId) return;
    if (scrolledToRef.current === selectedId) return;
    const row = rowRefs.current.get(selectedId);
    if (!row) return;
    scrolledToRef.current = selectedId;
    row.scrollIntoView?.({ block: 'nearest' });
  });
  // The keyboard's row takes focus (the ring shows where j/k are) and comes
  // into view; the page moves it, so a moved cursor is always a new id.
  useEffect(() => {
    if (!cursorId) return;
    const row = rowRefs.current.get(cursorId);
    if (!row) return;
    row.focus({ preventScroll: true });
    row.scrollIntoView?.({ block: 'nearest' });
  }, [cursorId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return source
      .filter((c) => {
        if (statusFilter === 'open' && c.status !== 'open') return false;
        if (statusFilter === 'in_progress' && c.status !== 'in_progress') return false;
        if (systemFilter === 'general' && c.system_id) return false;
        if (systemFilter && systemFilter !== 'general' && c.system_id !== systemFilter)
          return false;
        if (tagFilter && !(c.tags ?? []).includes(tagFilter)) return false;
        if (waitingOnly && waitingSince(c) === null) return false;
        if (mineOnly && (!myAdminId || c.assigned_admin_id !== myAdminId)) return false;
        if (agentFilter === 'me' && (!myAdminId || c.assigned_admin_id !== myAdminId))
          return false;
        if (agentFilter === 'unassigned' && c.assigned_admin_id) return false;
        if (
          agentFilter &&
          agentFilter !== 'me' &&
          agentFilter !== 'unassigned' &&
          c.assigned_admin_id !== agentFilter
        )
          return false;
        if (!q) return true;
        return [
          c.user_name,
          c.user_phone,
          c.context_rental_id,
          c.context_hardware_id,
          c.assigned_admin_name,
          c.system_name,
          c.organization_name,
          c.id,
          c.conversation_id,
          c.user_id,
        ].some((v) => v?.toLowerCase().includes(q));
      })
      .sort(compareConversations);
  }, [
    source,
    search,
    statusFilter,
    systemFilter,
    tagFilter,
    agentFilter,
    waitingOnly,
    mineOnly,
    myAdminId,
  ]);

  // The page keeps the rows' order for j/k/n; it hears every change of it.
  const orderedIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const onOrderChangeRef = useRef(onOrderChange);
  onOrderChangeRef.current = onOrderChange;
  useEffect(() => {
    onOrderChangeRef.current?.(orderedIds);
  }, [orderedIds]);
  // Roving tabindex: one row is in the tab order, the cursor's, else the
  // open conversation's, else the first.
  const tabRowId = cursorId ?? selectedId ?? orderedIds[0];

  const counts = useMemo(
    () => ({
      all: conversations.length,
      open: conversations.filter((c) => c.status === 'open').length,
      in_progress: conversations.filter((c) => c.status === 'in_progress').length,
      waiting: conversations.filter((c) => waitingSince(c) !== null).length,
      mine: myAdminId
        ? conversations.filter((c) => c.assigned_admin_id === myAdminId).length
        : 0,
    }),
    [conversations, myAdminId]
  );

  const live = TRANSPORT[transport];
  const loading =
    statusFilter === 'closed'
      ? closedLoading && source.length === 0
      : statusFilter === 'assistant'
      ? !assistantLoaded && source.length === 0
      : !loaded;
  const assistantOn = statusFilter === 'assistant';
  const filtering = Boolean(
    search.trim() || systemFilter || tagFilter || agentFilter || waitingOnly || mineOnly
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack spacing={1.5} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
            <Typography variant="subtitle1">Queue</Typography>
            <Tooltip
              title={
                <Box component="dl" sx={{ m: 0, display: 'grid', gap: '2px 12px' }}>
                  {KEYBOARD_SHORTCUTS.map(([keys, does]) => (
                    <React.Fragment key={keys}>
                      <Box component="dt" sx={{ gridColumn: 1, fontFamily: 'monospace' }}>
                        {keys}
                      </Box>
                      <Box component="dd" sx={{ gridColumn: 2, m: 0 }}>
                        {does}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              }
            >
              <IconButton size="small" aria-label="Keyboard shortcuts" sx={{ p: 0.25 }}>
                <HelpOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Tooltip title="Riders waiting for an answer">
            <Chip
              size="small"
              label={`Waiting (${counts.waiting})`}
              color={waitingOnly ? 'primary' : 'default'}
              variant={waitingOnly ? 'filled' : 'outlined'}
              onClick={() => setWaitingOnly((v) => !v)}
              aria-pressed={waitingOnly}
              sx={{ mr: 1 }}
              data-testid="waiting-filter"
            />
          </Tooltip>
          {myAdminId && (
            <Tooltip title="Chats assigned to you">
              <Chip
                size="small"
                label={`Mine (${counts.mine})`}
                color={mineOnly ? 'primary' : 'default'}
                variant={mineOnly ? 'filled' : 'outlined'}
                onClick={() => setMineOnly((v) => !v)}
                aria-pressed={mineOnly}
                sx={{ mr: 1 }}
                data-testid="mine-filter"
              />
            </Tooltip>
          )}
          {SHOW_ASSISTANT && (
            <Tooltip title="Chats the Assistant is answering: read-only until you take one over">
              <Chip
                size="small"
                icon={<SmartToyOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                label={
                  assistantOn || assistantConversations.length
                    ? `Assistant (${assistantConversations.length})`
                    : 'Assistant'
                }
                color={assistantOn ? 'secondary' : 'default'}
                variant={assistantOn ? 'filled' : 'outlined'}
                onClick={() => onStatusFilterChange(assistantOn ? 'all' : 'assistant')}
                aria-pressed={assistantOn}
                sx={{ mr: 1 }}
                data-testid="assistant-filter"
              />
            </Tooltip>
          )}
          <Tooltip title={live.title}>
            <Chip
              size="small"
              variant="outlined"
              color={live.color}
              icon={<FiberManualRecordIcon sx={{ fontSize: '10px !important' }} />}
              label={live.label}
              data-testid="transport"
            />
          </Tooltip>
        </Stack>

        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            placeholder="Search name, phone, rental"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search conversations' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            size="small"
            color={filtersOpen || tagFilter || agentFilter ? 'primary' : 'inherit'}
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
            data-testid="filters-toggle"
          >
            Filters
          </Button>
        </Stack>

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          fullWidth
          size="small"
          onChange={(_, v) => v && onStatusFilterChange(v)}
          aria-label="Status"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 0.5,
              py: 0.5,
              typography: 'caption',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ToggleButton value="all">
            <Tooltip title="Open and in progress">
              <span>All ({counts.all})</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="open">Open ({counts.open})</ToggleButton>
          <ToggleButton value="in_progress">
            In progress ({counts.in_progress})
          </ToggleButton>
          <ToggleButton value="closed">Closed</ToggleButton>
        </ToggleButtonGroup>

        {/* System on its own row: three selects in 360 px cut every value
            ("Unassigned" included) to a few letters. */}
        <TextField
          select
          size="small"
          fullWidth
          label="System"
          value={systemFilter}
          onChange={(e) => setSystemFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ 'data-testid': 'system-filter' }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="general">General</MenuItem>
          {systemOptions.map(([id, name]) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <Collapse in={filtersOpen} unmountOnExit>
          <Stack direction="row" gap={1}>
            <Autocomplete<TagFilterOption, false, false, false>
              size="small"
              sx={{ flex: 1, minWidth: 0 }}
              options={tagOptions}
              value={tagValue}
              onChange={(_, option) => setTagFilter(option?.id ?? '')}
              groupBy={(option) => option.groupId}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="No tags on these conversations"
              renderGroup={(params) => (
                <li key={params.key}>
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    sx={{ px: 1.5, py: 0.5, fontWeight: 600 }}
                    data-testid="tag-filter-group"
                  >
                    {tagGroupNames.get(params.group) ?? params.group}
                  </Typography>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id} data-testid="tag-filter-option">
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tag"
                  placeholder="All"
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  inputProps={{ ...params.inputProps, 'aria-label': 'Tag' }}
                />
              )}
            />
            <TextField
              select
              size="small"
              label="Agent"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              sx={{ flex: 1, minWidth: 136 }}
              inputProps={{ 'data-testid': 'agent-filter' }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Anyone</MenuItem>
              {myAdminId && <MenuItem value="me">Me</MenuItem>}
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {agents.map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Collapse>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }} data-testid="list-loading">
            <CircularProgress size={24} />
          </Stack>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }} data-testid="list-empty">
            <Typography variant="body2" color="text.secondary">
              {filtering
                ? 'No conversation matches these filters'
                : statusFilter === 'closed'
                ? 'No closed conversations'
                : assistantOn
                ? 'The Assistant is not answering anyone right now.'
                : 'No riders are waiting. New conversations appear here.'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding role="listbox" aria-label="Conversations">
            {filtered.map((conv) => {
              const unread = selectedId === conv.id ? 0 : unreadByConv[conv.id] || 0;
              const urgent = conv.priority === 'urgent';
              const wait = queueWait(conv, now);
              const closed = conv.status === 'closed';
              const preview = previewLine(conv);
              const name = rowName(conv);
              const timeIso = closed
                ? conv.closed_at ?? conv.updated_on
                : conv.updated_on;
              return (
                <ListItemButton
                  key={conv.id}
                  ref={rowRef(conv.id)}
                  role="option"
                  aria-selected={selectedId === conv.id}
                  tabIndex={tabRowId === conv.id ? 0 : -1}
                  selected={selectedId === conv.id}
                  onClick={() => onSelect(conv)}
                  divider
                  alignItems="flex-start"
                  sx={{
                    gap: 1.5,
                    py: 1.5,
                    borderLeft: 3,
                    borderLeftColor: urgent && !closed ? 'error.main' : 'transparent',
                  }}
                  data-testid="conversation-row"
                >
                  <Badge
                    color="error"
                    overlap="circular"
                    badgeContent={unread}
                    max={99}
                    data-testid={unread ? 'unread-badge' : undefined}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: urgent && !closed ? 'error.main' : 'primary.main',
                        fontSize: 14,
                      }}
                    >
                      {riderLabel(conv)[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="baseline" gap={1}>
                      <Typography
                        variant="body2"
                        noWrap
                        color={name.pending ? 'text.secondary' : 'text.primary'}
                        sx={{
                          flex: 1,
                          fontWeight: name.pending ? 400 : unread ? 700 : 600,
                        }}
                        data-testid="row-name"
                        data-rider={name.state}
                      >
                        {name.label}
                      </Typography>
                      <Tooltip title={formatDateTime(timeIso)}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatAgo(timeIso, now)}
                        </Typography>
                      </Tooltip>
                    </Stack>
                    {preview && (
                      <Typography
                        variant="body2"
                        color={unread ? 'text.primary' : 'text.secondary'}
                        noWrap
                        component="div"
                        data-testid="row-preview"
                      >
                        {preview}
                      </Typography>
                    )}
                    {(multiSystem || conv.assigned_admin_name) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        component="div"
                        data-testid="row-meta"
                      >
                        {[
                          multiSystem ? systemLabel(conv) : null,
                          conv.assigned_admin_name,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                    <Stack direction="row" gap={0.5} mt={0.75} flexWrap="wrap">
                      {statusFilter === 'all' && (
                        <Chip
                          size="small"
                          label={statusLabel(conv.status)}
                          color={STATUS_CHIP[conv.status] ?? 'default'}
                          variant="outlined"
                        />
                      )}
                      {urgent && !closed && (
                        <Chip size="small" label="Urgent" color="error" />
                      )}
                      {SHOW_ASSISTANT && conv.handled_by === 'assistant' && !closed && (
                        <Chip
                          size="small"
                          icon={<SmartToyOutlinedIcon />}
                          label="Assistant"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      {SHOW_ASSISTANT &&
                        conv.escalation_reason &&
                        conv.handled_by !== 'assistant' && (
                          <Tooltip title={`Escalated: ${conv.escalation_reason}`}>
                            <Chip
                              size="small"
                              label="Escalated"
                              variant="outlined"
                              color="warning"
                              data-testid="escalated-chip"
                            />
                          </Tooltip>
                        )}
                      {wait && (
                        <Tooltip
                          title={`Rider waiting for an answer since ${formatDateTime(
                            wait.since
                          )}`}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color={SLA_CHIP_COLOR[wait.level]}
                            label={wait.label}
                            data-testid="sla-chip"
                            data-level={wait.level}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}
